import { Fragment } from "react";
import { markOf, type SearchHit } from "../settings/search";

/**
 * ═══ LE CHAMP DE RECHERCHE, EN TÊTE DU PANNEAU ═══
 *
 * Au-dessus des onglets, parce qu'il cherche dans les deux : « Le site » et tous les
 * écrans. Tant qu'il contient quelque chose, les résultats prennent la place des
 * familles ; le vider rend le panneau tel qu'on l'avait laissé.
 *
 * Entrée ouvre le premier résultat, Échap vide le champ.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function SearchField({
  query,
  onQuery,
  onSubmit,
}: {
  query: string;
  onQuery: (q: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="psearch">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </svg>
      <input
        type="search"
        value={query}
        placeholder="Chercher un réglage, ou un texte du site"
        aria-label="Chercher un réglage"
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSubmit();
          }
          if (e.key === "Escape") onQuery("");
        }}
      />
    </div>
  );
}

function Marked({ text, query }: { text: string; query: string }) {
  const mark = markOf(text, query);
  if (!mark) return <>{text}</>;
  return (
    <>
      {mark.before}
      <mark>{mark.match}</mark>
      {mark.after}
    </>
  );
}

/** Où se règle ce qui masque, quand ce n'est pas dans la famille du résultat. */
function placed(where: string | undefined): string {
  return where ? ` : il se règle dans ${where}` : "";
}

/** Ce qu'un résultat masqué demande, dit en une ligne. */
function hiddenNote(hit: SearchHit): string | null {
  switch (hit.hidden?.by) {
    case "step":
      return "Masqué : cette étape n'est pas dans le parcours.";
    case "switch":
      return `Masqué tant que « ${hit.hidden.label} » est ${hit.hidden.negated ? "activé" : "désactivé"}${placed(hit.hidden.where)}.`;
    case "mode":
      return `Masqué tant que « ${hit.hidden.label} » est réglé sur « ${hit.hidden.option} »${placed(hit.hidden.where)}.`;
    case "other":
      return "Masqué par un autre réglage de sa famille.";
    default:
      return null;
  }
}

export function SearchResults({
  query,
  hits,
  onPick,
}: {
  query: string;
  hits: ReadonlyArray<SearchHit>;
  onPick: (hit: SearchHit) => void;
}) {
  if (hits.length === 0)
    return (
      <div className="presults">
        <p className="presults__n">
          Aucun réglage ne correspond à « {query.trim()} ».
        </p>
      </div>
    );

  return (
    <div className="presults">
      <p className="presults__n" role="status">
        {hits.length === 1 ? "1 réglage" : `${hits.length} réglages`}
      </p>
      {hits.map((hit, i) => {
        const note = hiddenNote(hit);
        return (
          <Fragment key={hit.setting.key}>
            {hit.screen !== hits[i - 1]?.screen && (
              <p className="presults__g">{hit.screen}</p>
            )}
            <button
              type="button"
              className="phit"
              onClick={() => onPick(hit)}
            >
              <b>
                <Marked text={hit.setting.label} query={query} />
              </b>
              <small>
                {hit.group}
                {hit.setting.section ? ` · ${hit.setting.section}` : ""}
              </small>
              {hit.shown && (
                <i>
                  Sur le site : « <Marked text={hit.shown} query={query} /> »
                </i>
              )}
              {note && <em>{note}</em>}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}
